import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import FeatureReductionCluster from "@arcgis/core/layers/support/FeatureReductionCluster";
import CustomContent from "@arcgis/core/popup/content/CustomContent";
import MapView from "@arcgis/core/views/MapView";
import { MutableRefObject, useContext, useEffect, useMemo } from "react";
import { IClashToForget, ObservationsContext } from "../../ObservationsContextContainer";
import { useGetObservationPopupLine } from "./useGetObservationPopupLine";
import ActionButton from "@arcgis/core/support/actions/ActionButton";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import { storageAvailable } from "../../../utils";
import { getClashToForgetFromFeature } from "./utils";
import { useTranslation } from "react-i18next";

function useSetObservationsClustering(view: MapView, pointsLayer: FeatureLayer, ref: MutableRefObject<boolean>) {
    const { getObservationLine } = useGetObservationPopupLine();
    const observationsContext = useContext(ObservationsContext);
    const { t, i18n } = useTranslation("observations");

    const measureThisAction = useMemo(() => new ActionButton({
        title: t("klusteriPopupinToiminto"),
        id: "forget-clustered-observations",
        type: "button",
        image: "",
        className: "hide-esri-default-action-icon"
    }), [t, i18n.language]);

    useEffect(() => {
        observationsContext.observationsState.allTaxons && observationsContext.observationsState.allCollections && ref.current && view.whenLayerView(pointsLayer).then(lv => {
            const layerView = lv;

            const getClusteredFeatures = async (objectId: any) => {
                const query = layerView.createQuery();

                query.aggregateIds = [objectId];

                const res = await layerView.queryFeatures(query);

                return res;
            };

            const customContentPromise = new CustomContent({
                outFields: ["*"],
                creator: async (event: any) => {
                    const res = await getClusteredFeatures(event.graphic.getObjectId());

                    let observationLines: string = "";

                    for (const feature of res.features) {
                        observationLines += getObservationLine(feature);
                    }

                    if (i18n.language === "en") {
                        return `<p>This location contains a total of ${res.features.length} observations.</p>
                            ${observationLines}
                        `;
                    }
                    else {
                        return `<p>Tämä sijainti sisältää yhteensä ${res.features.length} lajihavaintoa.</p>
                                ${observationLines}
                            `;
                    }
                }
            });

            const clusterConfig = new FeatureReductionCluster({
                popupTemplate: {
                    title: t("klusteriPopupinOtsikko"),
                    content: [customContentPromise],
                    actions: storageAvailable("localStorage") ? [measureThisAction] : []
                },
                clusterMinSize: "16",
                clusterRadius: "50px"
            });

            pointsLayer.featureReduction = clusterConfig;

            reactiveUtils.on(
                () => view.popup,
                "trigger-action",
                async (event) => {
                    if (event.action.id === "forget-clustered-observations") {
                        const feature = view.popup.selectedFeature;

                        const res = await getClusteredFeatures(feature.getObjectId());

                        const clashes: IClashToForget[] = res.features.filter(f => f.attributes && f.attributes["id"]).map(f => getClashToForgetFromFeature(f));

                        observationsContext.observationsStateDispatch({ type: "setClashesToForget", clashes });
                    }
                });
        }, (error) => {
            console.log('some error when trying to set up cluster popup: ', error)
        });
    }, [ref.current, observationsContext.observationsState.allTaxons, observationsContext.observationsState.allCollections, t, i18n.language]);
};

export default useSetObservationsClustering;

